import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FiCopy, FiRefreshCw, FiDownload, FiInfo } from "react-icons/fi";
import style from "../monetization/Monetization.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import useUrlTab from "../../hooks/useUrlTab";
import {
  getMyCreatorOverview,
  getMyCreatorRevenue,
  getMyCreatorRevenueBreakdown,
  getMyCreatorEarningsBreakdown,
  getMyCreatorContentAnalytics,
  getMyCreatorWeeklyWatchtime,
  getMyCreatorPayouts,
  getMyCreatorPayoutAccount,
  startMyCreatorOnboarding,
  exportMyPayoutLedgerXlsx,
} from "../../api/HttpApis";

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const hrs = (m) => {
  const n = Number(m) || 0;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const body = (res) => res?.data ?? res ?? {};
const CUR_YEAR = new Date().getUTCFullYear();
const CHART_TOOLTIP = { background: "#2a2a2a", border: "1px solid #444", color: "#fff", borderRadius: 8 };
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// period is "YYYY-MM" from the backend — render as "Sep 2026", not a bare "09".
const monthLabel = (period) => {
  const [y, m] = String(period || "").split("-");
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${MONTH_LABELS[idx]} ${y}` : period;
};

// Every status a Payout OR AffiliateCommission/TVOD ledger row can actually
// have, shared by the Payouts tab and the Earnings Ledger table so the same
// status always looks/reads the same everywhere on this dashboard. Falling
// through to "pending" for an unmapped status (on_hold/failed/reversed/
// processing) would show a creator a hopeful orange chip for money that's
// actually stuck or reversed.
const STATUS_CHIP_CLASS = {
  pending: "chipPending",
  approved: "chipApproved",
  paid: "chipPaid",
  earned: "chipApproved",
  booked: "chipApproved",
  holding: "chipHolding",
  on_hold: "chipOn_hold",
  failed: "chipFailed",
  reversed: "chipFailed",
  processing: "chipApproved",
};
const chipCls = (s) => style[STATUS_CHIP_CLASS[s] || "chipPending"];
// Internal wording + a one-line plain-language explanation for the hover
// tooltip, so "Holding" / "Earned" aren't left to guesswork.
const STATUS_INFO = {
  earned: { label: "Earned", info: "You've earned this — it's not bundled into a payout yet." },
  booked: { label: "Earned", info: "You've earned this — it's not bundled into a payout yet." },
  holding: { label: "Held", info: "Bundled into a payout, but not released yet — still inside the hold period or below the minimum payout amount." },
  pending: { label: "Pending", info: "Released and waiting to be sent." },
  approved: { label: "Approved", info: "Reviewed and approved — about to be sent." },
  processing: { label: "Processing", info: "Payment is being sent right now." },
  paid: { label: "Paid", info: "Sent to your payout account." },
  on_hold: { label: "On Hold", info: "Couldn't be sent — your payout account needs attention." },
  failed: { label: "Failed", info: "The payment attempt failed. Support has been notified." },
  reversed: { label: "Reversed", info: "This payment was reversed." },
};
const statusLabel = (s) => STATUS_INFO[s]?.label || s;
const statusInfo = (s) => STATUS_INFO[s]?.info || null;

function DashboardTab({ overview, onCopy, onRefresh }) {
  const ref = overview?.referral;
  const c = overview?.cards || {};
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);

  const handleExportLedger = useCallback(async () => {
    setLedgerBusy(true);
    try {
      await exportMyPayoutLedgerXlsx({});
      showToast("Your ledger export started.", "success");
    } catch {
      showToast("Could not export your ledger.", "error");
    } finally {
      setLedgerBusy(false);
    }
  }, []);

  const toggleBreakdown = useCallback(async () => {
    const next = !showBreakdown;
    setShowBreakdown(next);
    if (next && !breakdown) {
      setBreakdownLoading(true);
      try {
        const res = await getMyCreatorEarningsBreakdown({});
        setBreakdown(body(res));
      } catch {
        showToast("Could not load your earnings breakdown.", "error");
      } finally {
        setBreakdownLoading(false);
      }
    }
  }, [showBreakdown, breakdown]);
  const cards = [
    { label: "Total Content", value: c.total_content ?? 0, hint: "Published", info: "Number of videos you've uploaded." },
    { label: "Referral Clicks", value: ref?.click_count ?? 0, hint: "All time", info: "How many times your referral link was opened." },
    { label: "Referral Signups", value: ref?.signup_count ?? 0, hint: "All time", info: "People who created an account through your link." },
    { label: "Conversions", value: ref?.conversion_count ?? 0, hint: "Paid", info: "Referred users who bought a paid plan — you earn commission on these." },
    // "Total Referrals" removed (2026-09): always exactly equal to "Referral
    // Signups" above — both count the same event (a signup via this
    // creator's link), just via two different code paths. Showing both was
    // a confusing duplicate, not a distinct metric.
    { label: "Referral Earning", value: money(c.monthly_referral_earning_cents), hint: "This month", info: "Your commission from referred subscriptions this month. Updates once the month closes — figures for the current, still-in-progress month appear here after it ends." },
    { label: "Total Subscribers", value: c.total_subscribers ?? 0, hint: "Active", info: "Your referred users with an active paid plan." },
    { label: "Total Watch Time", value: hrs(c.total_watch_minutes), hint: "Lifetime", info: "Total time people watched your content." },
    { label: "Watch Time Earning", value: money(c.monthly_watch_earning_cents), hint: "This month", info: "Your share of the watch-time pool this month. Updates once the month closes — figures for the current, still-in-progress month appear here after it ends." },
    // "TVOD Earning" card hidden from the creator dashboard (2026-09). TVOD
    // revenue is still included in Lifetime Earnings / Pending Payout.
    { label: "Lifetime Earnings", value: money(c.lifetime_earnings_cents), info: "All your earnings to date (referral + watch pool + TVOD)." },
    { label: "Pending Payout", value: money(c.pending_payout_cents), info: "Everything on its way to you — includes money still being held below, once it's ready." },
  ];
  const heldReasonText =
    c.held_blocked_by === "hold_period"
      ? "waiting out a short hold period after purchase (in case of a refund)"
      : c.held_blocked_by === "below_minimum"
        ? `${money(c.held_shortfall_cents)} short of the ${money(c.payout_threshold_cents)} minimum payout amount`
        : null;
  // Only shown when there's genuinely something held — a creator with
  // nothing held should never see an empty/confusing "$0.00" card.
  if (c.held_earnings_cents > 0) {
    cards.push({
      label: "Held for Review",
      value: money(c.held_earnings_cents),
      hint: c.held_since_period ? `since ${c.held_since_period}` : undefined,
      info: heldReasonText
        ? `Earned, but not paid out yet — ${heldReasonText}.`
        : "Earned, but not paid out yet.",
    });
  }

  return (
    <>
      {ref && (
        <div className={style.refBanner}>
          <div>
            <p className={style.refTitle}>Your Referral Link</p>
            <p className={style.refSub}>Share this link to earn from every subscription it brings in.</p>
          </div>
          <div className={style.refRight}>
            <input className={style.refInput} readOnly value={ref.link || ""} />
            <button className={style.btn} onClick={() => onCopy(ref.link)}>
              <FiCopy className={style.iconMr6} /> Get Link
            </button>
          </div>
        </div>
      )}

      <div className={style.flexEndMb16}>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={onRefresh}>
          <FiRefreshCw className={style.iconMr6} /> Refresh
        </button>
      </div>

      <div className={style.cardGrid}>
        {cards.map((it) => (
          <div className={style.card} key={it.label}>
            <div className={`${style.cardLabel} ${style.labelWithInfo}`}>
              {it.label}
              {it.info && (
                <span title={it.info} className={style.infoIcon}>
                  <FiInfo size={13} />
                </span>
              )}
            </div>
            <div className={style.cardValue}>{it.value}</div>
            {it.hint && <div className={style.cardHint}>{it.hint}</div>}
          </div>
        ))}
      </div>

      <div className={style.flexEndMb16} style={{ gap: 8 }}>
        <button
          className={`${style.btn} ${style.btnGhost}`}
          onClick={handleExportLedger}
          disabled={ledgerBusy}
          title="Every earning of yours as one file — Watch Pool, Affiliate, and TVOD, each on its own sheet, plus what's paid, pending, or held."
        >
          {ledgerBusy ? "Preparing…" : "Download My Ledger (.xlsx)"}
        </button>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={toggleBreakdown}>
          {showBreakdown ? "Hide" : "Where did this month's earnings come from?"}
        </button>
      </div>

      {showBreakdown && (
        <div className={style.tableWrap}>
          <div className={style.sectionHeaderPad}>
            <h3 className={`${style.chartTitle} ${style.chartTitleTight}`}>This Month's Earnings — the real breakdown</h3>
            <p className={style.chartSub}>{breakdown ? `Total: ${money(breakdown.total_cents)}` : ""}</p>
          </div>
          {breakdownLoading ? (
            <div className={style.empty}>Loading…</div>
          ) : breakdown ? (
            <div className={style.tableScroll}>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Detail</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.watch_pool.amount_cents > 0 && (
                    <tr>
                      <td>Watch Pool</td>
                      <td>{breakdown.watch_pool.counted_minutes} min watched</td>
                      <td>{money(breakdown.watch_pool.amount_cents)}</td>
                    </tr>
                  )}
                  {breakdown.affiliate.items.map((it, i) => (
                    <tr key={`aff-${i}`}>
                      <td>Affiliate</td>
                      <td>{it.label}</td>
                      <td>{money(it.amount_cents)}</td>
                    </tr>
                  ))}
                  {breakdown.tvod.items.map((it, i) => (
                    <tr key={`tvod-${i}`}>
                      <td>TVOD</td>
                      <td>
                        {it.label} ({it.access_type === "buy" ? "purchased" : "rented"})
                      </td>
                      <td>{money(it.amount_cents)}</td>
                    </tr>
                  ))}
                  {breakdown.watch_pool.amount_cents === 0 &&
                    breakdown.affiliate.items.length === 0 &&
                    breakdown.tvod.items.length === 0 && (
                      <tr>
                        <td colSpan={3} className={style.empty}>
                          No earnings recorded for this period yet.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}

function AnalyticsTab({ onDownload }) {
  const [loading, setLoading] = useState(true);
  const [weekly, setWeekly] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [content, setContent] = useState([]);
  const [topContent, setTopContent] = useState([]);
  const [contentWatchTime, setContentWatchTime] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [ledgerMonth, setLedgerMonth] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [wk, rev, cont, brk] = await Promise.all([
          getMyCreatorWeeklyWatchtime().catch(() => null),
          getMyCreatorRevenue({ year: CUR_YEAR }).catch(() => null),
          getMyCreatorContentAnalytics({ year: CUR_YEAR }).catch(() => null),
          getMyCreatorRevenueBreakdown().catch(() => null),
        ]);
        if (!active) return;
        setWeekly((body(wk)?.days || []).map((d) => ({ name: d.label, hours: +(d.minutes / 60).toFixed(2) })));
        setRevenue(body(rev));
        setBreakdown(body(brk));
        const contData = body(cont);
        setContent(contData?.content || []);
        setTopContent(contData?.top_content || []);
        setContentWatchTime(contData?.content_watch_time || []);
        const ledgerData = contData?.ledger || null;
        setLedger(ledgerData);
        setLedgerMonth(ledgerData?.current_month?.month ?? ledgerData?.by_month?.[0]?.month ?? null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <div className={style.loading}>Loading analytics…</div>;

  const monthly = (revenue?.trend || []).map((t) => ({
    name: monthLabel(t.period),
    affiliate: (t.affiliate_cents || 0) / 100,
    watch: (t.watch_pool_cents || 0) / 100,
    tvod: (t.tvod_cents || 0) / 100,
  }));

  return (
    <>
      <div className={style.flexEndMb14}>
        <button className={`${style.btn} ${style.btnGhost}`} onClick={onDownload}>
          <FiDownload className={style.iconMr6} /> Download Report
        </button>
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Weekly Watchtime — Live Activity</h3>
        <p className={style.chartSub}>Hours people watched your content in the last 7 days</p>
        {weekly.every((d) => !d.hours) && (
          <p className={style.chartSub} style={{ marginTop: -6 }}>
            No watch activity in the last 7 days. Older activity still counts toward your
            Lifetime totals and monthly earnings below — this chart only looks at the past week.
          </p>
        )}
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={weekly}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="name" stroke="#fff" />
            <YAxis stroke="#fff" />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => `${v} hrs`} />
            <Line type="monotone" dataKey="hours" stroke="#b87333" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <p className={style.chartSub} style={{ marginTop: 8 }}>
          Note: this is live viewing activity, not your earnings. It turns into a payout
          after the next monthly payout run.
        </p>
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Monthly Revenue</h3>
        <p className={style.chartSub}>
          Your {monthly.length} {monthly.length === 1 ? "month" : "months"} of revenue, by source — Affiliate (referrals), Watch Pool, and TVOD (Buy/Rent)
        </p>
        {monthly.length === 0 ? (
          <div className={style.empty}>No revenue data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthly} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="name" stroke="#fff" />
              <YAxis stroke="#fff" />
              <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => `$${Number(v).toFixed(2)}`} />
              <Bar dataKey="affiliate" fill="#b87333" name="Affiliate" radius={[4, 4, 0, 0]} maxBarSize={56} />
              <Bar dataKey="watch" fill="#e0b07a" name="Watch Pool" radius={[4, 4, 0, 0]} maxBarSize={56} />
              <Bar dataKey="tvod" fill="#6f9ceb" name="TVOD" radius={[4, 4, 0, 0]} maxBarSize={56} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>This Month's Referral Commission — Full Breakdown</h3>
        <p className={style.chartSub}>
          Every referred subscriber's payment, and exactly what it turned into after Stripe's processing fee.
        </p>
        {!breakdown || breakdown.rows.length === 0 ? (
          <div className={style.empty}>No referral commission this month yet.</div>
        ) : (
          <>
            <div className={style.tableWrap}>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Charged</th>
                    <th>Stripe's fee</th>
                    <th>Net received</th>
                    <th>Your {`${breakdown.rows[0]?.commission_percent ?? ""}%`}</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.rows.map((r) => (
                    <tr key={r.id}>
                      <td>{new Date(r.date).toLocaleDateString()}</td>
                      <td>{money(r.gross_charge_cents)}</td>
                      <td>{r.fee_finalized ? `−${money(r.stripe_fee_cents)}` : "—"}</td>
                      <td>
                        {r.fee_finalized ? (
                          money(r.net_cents)
                        ) : (
                          <span title="Stripe hasn't finished processing this payment yet — the amount below is provisional and will update once it settles, usually within moments.">
                            still settling
                          </span>
                        )}
                      </td>
                      <td>{money(r.commission_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={style.chartSub} style={{ marginTop: 10 }}>
              Total this month: {money(breakdown.totals.gross_charge_cents)} charged, {money(breakdown.totals.stripe_fee_cents)} in Stripe fees, {money(breakdown.totals.commission_cents)} earned by you.
            </p>
          </>
        )}
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Best Performing Content</h3>
        <p className={style.chartSub}>Videos that performed best this month</p>
        {content.length === 0 ? (
          <div className={style.empty}>No watch data this month.</div>
        ) : (
          <div>
            {content.map((c, i) => (
              <div className={style.bestRow} key={c.video_meta_id}>
                <span className={style.bestRank}>{i + 1}</span>
                {c.thumbnail ? (
                  <img className={style.bestThumb} src={c.thumbnail} alt={c.title} />
                ) : (
                  <div className={style.bestThumb} />
                )}
                <div className={style.bestInfo}>
                  <div className={style.bestName}>{c.title}</div>
                  <div className={style.bestMeta}>
                    {hrs(c.watch_minutes)} watched · {c.view_count} views
                  </div>
                </div>
                <div className={style.bestEarn}>{money(c.estimated_earning_cents)} earned</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Top Content</h3>
        <p className={style.chartSub}>Your top ranked content this month</p>
        {topContent.length === 0 ? (
          <div className={style.empty}>No top content this month.</div>
        ) : (
          <div>
            {topContent.map((c, i) => (
              <div className={style.bestRow} key={c.video_meta_id}>
                <span className={style.bestRank}>{i + 1}</span>
                {c.thumbnail ? (
                  <img className={style.bestThumb} src={c.thumbnail} alt={c.title} />
                ) : (
                  <div className={style.bestThumb} />
                )}
                <div className={style.bestInfo}>
                  <div className={style.bestName}>{c.title}</div>
                  <div className={style.bestMeta}>
                    {c.type} · {hrs(c.watch_minutes)} watched · {c.view_count} views
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Content Watch Time</h3>
        <p className={style.chartSub}>Watch time breakdown per video this month</p>
        <div className={style.tableWrap}>
          <table className={style.table}>
            <thead>
              <tr><th>Title</th><th>Type</th><th>Watch Time</th><th>Views</th></tr>
            </thead>
            <tbody>
              {contentWatchTime.length === 0 ? (
                <tr><td colSpan={4} className={style.empty}>No watch time data yet.</td></tr>
              ) : (
                contentWatchTime.map((c) => (
                  <tr key={c.video_meta_id}>
                    <td>{c.title}</td>
                    <td>{c.type}</td>
                    <td>{hrs(c.watch_minutes)}</td>
                    <td>{c.view_count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(() => {
        const byMonth = ledger?.by_month || [];
        const currentMonthNum = ledger?.current_month?.month ?? null;
        const activeMonth = byMonth.find((m) => m.month === ledgerMonth) || ledger?.current_month || null;
        const entries = activeMonth?.entries || [];
        const fromWhom = (l) => {
          if (l.type === "affiliate_commission") return l.from?.subscriber?.name || l.from?.subscriber?.email || "—";
          if (l.type === "payout") return "Your commissions above";
          if (l.type === "watch_pool") return "Watch-time pool";
          if (l.type === "tvod") return l.from?.title || "Platform";
          return "Platform";
        };
        return (
          <div className={style.chartCard}>
            <div className={style.spaceBetweenWrap}>
              <div>
                <h3 className={style.chartTitle}>Earnings Ledger</h3>
                <p className={style.chartSub}>Your earnings and payout transactions, by month</p>
              </div>
              <select
                className={`${style.select} ${style.selectAuto}`}
                value={ledgerMonth ?? ""}
                onChange={(e) => setLedgerMonth(Number(e.target.value))}
              >
                {byMonth.map((m) => (
                  <option key={m.month} value={m.month}>
                    {m.label}
                    {m.month === currentMonthNum ? " (Current)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <p className={style.chartSub} style={{ marginTop: 4 }}>
              Not additive: "Affiliate Commission" / "Tvod" / "Watch Pool" rows are when you earned
              that money, and each "Payout" row is that same money grouped for sending — don't add
              the Payout amount on top of the earning rows above it. Hover a Status chip to see what
              it means.
            </p>
            <div className={style.tableWrap}>
              <table className={style.table}>
                <thead>
                  <tr><th>Date</th><th>Type</th><th>From</th><th>Description</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={6} className={style.empty}>No ledger entries for {activeMonth?.label || "this month"}.</td></tr>
                  ) : (
                    entries.map((l, i) => (
                      <tr key={i}>
                        <td>{l.date ? new Date(l.date).toLocaleDateString() : "—"}</td>
                        <td className={style.capitalize}>{l.type?.replace(/_/g, " ")}</td>
                        <td>{fromWhom(l)}</td>
                        <td>{l.description}</td>
                        <td>{money(l.amount_cents)}</td>
                        <td>
                          <span className={`${style.chip} ${chipCls(l.status)}`} title={statusInfo(l.status) || undefined}>
                            {statusLabel(l.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

    </>
  );
}

const SUBSCRIBERS_PER_PAGE = 10;

function SubscribersTab() {
  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const cont = await getMyCreatorContentAnalytics({ year: CUR_YEAR }).catch(() => null);
        if (!active) return;
        setSubscribers(body(cont)?.subscribers || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <div className={style.loading}>Loading subscribers…</div>;

  const lastPage = Math.max(1, Math.ceil(subscribers.length / SUBSCRIBERS_PER_PAGE));
  const pageItems = subscribers.slice((page - 1) * SUBSCRIBERS_PER_PAGE, page * SUBSCRIBERS_PER_PAGE);

  return (
    <div className={style.chartCard}>
      <h3 className={style.chartTitle}>Subscribers</h3>
      <p className={style.chartSub}>People subscribed through your referral link</p>
      <div className={style.tableWrap}>
        <table className={style.table}>
          <thead>
            <tr><th>Subscriber</th><th>Plan</th><th>Amount</th><th>Platform</th><th>Purchase Date</th><th>Expires</th><th>Status</th></tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr><td colSpan={7} className={style.empty}>No subscribers yet.</td></tr>
            ) : (
              pageItems.map((s, i) => (
                <tr key={i}>
                  <td>{s.subscriber?.name || s.subscriber?.email || "—"}</td>
                  <td>{s.plan?.name}</td>
                  <td>${Number(s.amount || 0).toFixed(2)} {s.currency}</td>
                  <td className={style.capitalize}>{s.platform}</td>
                  <td>{s.purchase_date ? new Date(s.purchase_date).toLocaleDateString() : "—"}</td>
                  <td>{s.expiration_date ? new Date(s.expiration_date).toLocaleDateString() : "—"}</td>
                  <td>
                    <span className={`${style.chip} ${s.status === "expired" ? style.chipFailed : style.chipPaid}`}>
                      {s.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {subscribers.length > SUBSCRIBERS_PER_PAGE && (
        <div className={`${style.toolbar} ${style.toolbarEndMt16}`}>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span className={style.pageInfo}>
            Page {page} of {lastPage}
          </span>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function PayoutsTab() {
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        getMyCreatorPayouts().catch(() => null),
        getMyCreatorPayoutAccount().catch(() => null),
      ]);
      setPayouts(body(p)?.payouts || []);
      setAccount(body(a) || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const res = await startMyCreatorOnboarding({});
      const url = body(res)?.url;
      if (url) window.location.href = url;
      else showToast("Could not start payout setup.", "error");
    } catch (e) {
      showToast(e?.response?.data?.message || "Could not start payout setup.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className={style.loading}>Loading…</div>;

  // Every status a Payout row can actually have — falling through to
  // "pending" for an unmapped one (on_hold/failed/reversed/processing) would
  // show a creator a hopeful orange chip for money that's actually stuck or
  // reversed.
  const chipCls = (s) =>
    ({
      pending: style.chipPending,
      approved: style.chipApproved,
      paid: style.chipPaid,
      holding: style.chipHolding,
      on_hold: style.chipOn_hold,
      failed: style.chipFailed,
      reversed: style.chipFailed,
      processing: style.chipApproved,
    }[s] || style.chipPending);
  // Internal wording translated to what a creator should actually read.
  const STATUS_LABELS = {
    holding: "Held",
    on_hold: "On Hold",
    failed: "Failed",
    reversed: "Reversed",
    processing: "Processing",
  };
  const statusLabel = (s) => STATUS_LABELS[s] || s;

  return (
    <>
      <div className={style.refBanner}>
        <div>
          <p className={style.refTitle}>Payout Account</p>
          <p className={style.refSub}>
            {account?.payouts_enabled ? "Your Stripe payout account is active." : "Connect your Stripe account to receive payouts."}
          </p>
        </div>
        <div className={style.refRight}>
          <span className={`${style.chip} ${account?.payouts_enabled ? style.chipPaid : style.chipPending}`}>
            {account?.onboarding_status || "not started"}
          </span>
          {!account?.payouts_enabled && (
            <button className={style.btn} onClick={startOnboarding} disabled={busy}>
              {busy ? "Opening…" : "Set up payouts"}
            </button>
          )}
        </div>
      </div>

      <div className={style.chartCard}>
        <h3 className={style.chartTitle}>Payout History</h3>
        <div className={style.tableWrap}>
          <table className={style.table}>
            <thead>
              <tr><th>Period</th><th>Amount</th><th>Status</th><th>Paid On</th></tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr><td colSpan={4} className={style.empty}>No payouts yet.</td></tr>
              ) : (
                payouts.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.period}
                      {p.covers_from && p.covers_from !== p.period && (
                        <div className={style.subEmail}>covers {p.covers_from} → {p.period}</div>
                      )}
                    </td>
                    <td>{money(p.amount_cents)}</td>
                    <td>
                      <span className={`${style.chip} ${chipCls(p.status)}`}>{statusLabel(p.status)}</span>
                      {p.status === "holding" && p.hold_release_at && (
                        <div className={style.subEmail}>
                          releases {new Date(p.hold_release_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const TABS = [
  { k: "dashboard", l: "Dashboard" },
  { k: "analytics", l: "Analytics" },
  { k: "subscribers", l: "Subscribers" },
  { k: "payouts", l: "Payouts" },
];

export default function CreatorMonetization() {
  // Active tab lives in the URL (?tab=subscribers) so a page reload keeps the
  // creator on the tab they were viewing instead of resetting to Dashboard.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = TABS.some((t) => t.k === tabParam) ? tabParam : "dashboard";
  const setTab = (k) => {
    const next = new URLSearchParams(searchParams);
    if (k === "dashboard") next.delete("tab");
    else next.set("tab", k);
    setSearchParams(next, { replace: true });
  };
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyCreatorOverview();
      setOverview(body(res));
    } catch (e) {
      showToast(e?.response?.data?.message || "Could not load dashboard.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      showToast("Referral link copied!", "success");
    } catch {
      showToast("Could not copy link.", "error");
    }
  };

  const handleDownload = async () => {
    try {
      // Creator-scoped export — the admin /payouts/export route needs
      // monetization.export, which creators don't have (403).
      await exportMyPayoutLedgerXlsx({ start: `${CUR_YEAR}-01-01` });
      showToast("Report download started", "success");
    } catch {
      showToast("Download failed", "error");
    }
  };

  const studioName = overview?.studio_name || "My Studio";

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <div>
          <h2 className={style.title}>{studioName}</h2>
          <p className={style.subtitle}>Your referrals, earnings, watch-time and payouts.</p>
        </div>
      </div>

      <div className={style.toolbar}>
        {TABS.map((t) => (
          <button
            key={t.k}
            className={`${style.btn} ${tab === t.k ? "" : style.btnGhost}`}
            onClick={() => setTab(t.k)}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading && tab === "dashboard" ? (
        <div className={style.loading}>Loading…</div>
      ) : (
        <>
          {tab === "dashboard" && (
            <DashboardTab overview={overview} onCopy={handleCopy} onRefresh={fetchOverview} />
          )}
          {tab === "analytics" && <AnalyticsTab onDownload={handleDownload} />}
          {tab === "subscribers" && <SubscribersTab />}
          {tab === "payouts" && <PayoutsTab />}
        </>
      )}
    </div>
  );
}
